import { useCallback, useEffect, useState } from "react";
import {
  dismissInstallPrompt,
  isInstallDismissed,
  isIosSafari,
  isStandaloneMode,
  type BeforeInstallPromptEvent,
} from "./pwa-utils";

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(() => isInstallDismissed());
  const [installed, setInstalled] = useState(() => isStandaloneMode());
  const iosSafari = isIosSafari();

  useEffect(() => {
    const onBeforeInstall = (event: BeforeInstallPromptEvent) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const onDisplayModeChange = () => {
      setInstalled(isStandaloneMode());
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.matchMedia("(display-mode: standalone)").addEventListener("change", onDisplayModeChange);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window
        .matchMedia("(display-mode: standalone)")
        .removeEventListener("change", onDisplayModeChange);
    };
  }, []);

  const dismiss = useCallback(() => {
    dismissInstallPrompt();
    setDismissed(true);
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return false;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (outcome === "accepted") {
      setInstalled(true);
      return true;
    }
    return false;
  }, [deferredPrompt]);

  const canInstall = !installed && !dismissed && (deferredPrompt !== null || iosSafari);

  return {
    canInstall,
    iosSafari,
    install,
    dismiss,
    hasNativePrompt: deferredPrompt !== null,
  };
}
